import type { Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { confirmStoreGeo, geoLabelFor, rejectStoreGeo, useStoreGeoLabels } from '../data/stores';
import { db } from '../db/db';
import { osmUrl } from '../lib/geo';

interface Props {
  storeId: Id;
  /** 'list' = tylko położenie z adresu czekające na potwierdzenie; 'sheet' = położenie zawsze, gdy jest. */
  variant: 'list' | 'sheet';
}

const linkCls = 'underline decoration-stone-300 underline-offset-2 dark:decoration-stone-600';
const actionCls = 'min-h-11 shrink-0 rounded-lg px-2 text-sm font-medium active:bg-stone-100 dark:active:bg-stone-800';

/**
 * Położenie sklepu: adres znaleziony przez geokoder (dane © OpenStreetMap) do potwierdzenia,
 * link do mapy i „To nie tu” (czyści współrzędne — wtedy liczą się tylko wizyty).
 */
export function StoreGeoNote({ storeId, variant }: Props) {
  // Prosto z bazy (tani odczyt jednego wiersza) — arkusz trzyma kopię sklepu z chwili otwarcia.
  const store = useLiveQuery(() => db.stores.get(storeId), [storeId]);
  const labels = useStoreGeoLabels();
  if (!store || !labels || store.lat == null || store.lng == null) return null;
  // Z adresu = geokoder, jeszcze niepotwierdzone („To tu” zmienia na 'here').
  const fromAddress = store.geoSource === 'address';
  if (variant === 'list' && !fromAddress) return null;
  // Opis miejsca jest tylko na telefonie, który pytał geokoder; inne pokazują ogólny napis.
  const label = geoLabelFor(store, labels.get(store.id));
  const osm = fromAddress || label !== null;
  const map = (
    <a href={osmUrl(store.lat, store.lng)} target="_blank" rel="noreferrer" className={linkCls}>
      {label ?? (fromAddress ? 'Położenie z adresu' : 'Położenie zapisane')}
    </a>
  );
  const attribution = osm && <span className="text-xs whitespace-nowrap text-stone-500">© OpenStreetMap</span>;
  const actions = (
    <>
      {fromAddress && (
        <button type="button" onClick={() => void confirmStoreGeo(store.id)} className={`${actionCls} text-brand-700 dark:text-brand-500`}>
          To tu
        </button>
      )}
      <button type="button" onClick={() => void rejectStoreGeo(store.id)} className={`${actionCls} text-stone-600 dark:text-stone-300`}>
        To nie tu
      </button>
    </>
  );

  if (variant === 'list') {
    return (
      <div className="flex basis-full items-center gap-1 border-t border-stone-100 py-1 pr-2 pl-4 text-sm dark:border-stone-800">
        <p className="min-w-0 flex-1 py-1">
          <span aria-hidden>📍 </span>
          {label && 'Znaleziono: '}
          {map} {attribution}
        </p>
        {actions}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 rounded-xl bg-stone-100 py-1 pr-1 pl-3 text-sm dark:bg-stone-800/60">
      <span aria-hidden className="mr-1">
        📍
      </span>
      <p className="min-w-0 flex-1 py-1">
        {map}
        {attribution && <span className="block">{attribution}</span>}
      </p>
      {actions}
    </div>
  );
}
